
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import ResponsiveTopNavigation from '../../components/navigation/ResponsiveTopNavigation';
import { base_url } from 'Backend/api';
import { FaStoreAlt } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  canShowOrderRatingButton,
  isOrderDelivered,
  isOrderRated,
} from 'utils/orderReviewStorage';
import { useOrderReviewModal } from 'contexts/OrderReviewModalContext';
import { getOrderStoreImage } from 'utils/resolveMediaUrl';
import AppSpinner from 'components/ui/AppSpinner';

const SwipeToDeleteOrderCard = ({
  children,
  onDelete,
  onCardClick,
  isHighlighted,
  runDemo = false,
  onDemoFinish,
}) => {
  const startXRef = useRef(0);
  const draggingRef = useRef(false);
  const demoPlayedRef = useRef(false);
  const [offsetX, setOffsetX] = useState(0);
  const isDeleteVisible = offsetX < -1;

  const MAX_REVEAL = 82;
  const OPEN_THRESHOLD = 36;

  const extractX = (event) => {
    if (event.touches && event.touches.length) return event.touches[0].clientX;
    if (event.changedTouches && event.changedTouches.length) return event.changedTouches[0].clientX;
    return event.clientX;
  };

  const handleStart = (event) => {
    draggingRef.current = true;
    startXRef.current = extractX(event);
  };

  const handleMove = (event) => {
    if (!draggingRef.current) return;
    const currentX = extractX(event);
    const delta = currentX - startXRef.current;
    const next = Math.max(-MAX_REVEAL, Math.min(0, delta));
    setOffsetX(next);
  };

  const handleEnd = () => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    setOffsetX((prev) => (Math.abs(prev) >= OPEN_THRESHOLD ? -MAX_REVEAL : 0));
  };

  const handleCardClick = () => {
    if (offsetX !== 0) {
      setOffsetX(0);
      return;
    }
    onCardClick();
  };

  useEffect(() => {
    if (!runDemo || demoPlayedRef.current) return undefined;

    demoPlayedRef.current = true;
    const t1 = setTimeout(() => setOffsetX(-56), 300);
    const t2 = setTimeout(() => setOffsetX(0), 1100);
    const t3 = setTimeout(() => {
      if (typeof onDemoFinish === "function") onDemoFinish();
    }, 1400);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [runDemo, onDemoFinish]);

  return (
    <div className="relative overflow-hidden rounded-[10px]">
      <div
        className={`absolute inset-y-0 right-0 flex w-[82px] items-center justify-center rounded-[10px] bg-[#F93209] transition-opacity duration-150 ${
          isDeleteVisible ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
            setOffsetX(0);
          }}
          className="flex flex-col items-center gap-1 text-white"
        >
          <Icon name="Trash2" size={18} />
          <span className="text-xs font-semibold">حذف</span>
        </button>
      </div>

      <div
        role="button"
        tabIndex={0}
        onTouchStart={handleStart}
        onTouchMove={handleMove}
        onTouchEnd={handleEnd}
        onMouseDown={handleStart}
        onMouseMove={(e) => {
          if (draggingRef.current) handleMove(e);
        }}
        onMouseUp={handleEnd}
        onMouseLeave={handleEnd}
        onClick={handleCardClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleCardClick();
          }
        }}
        className={`relative transition-transform duration-150 ${isHighlighted ? "ring-2 ring-orange-500 bg-orange-50 rounded-lg" : ""}`}
        style={{ transform: `translateX(${offsetX}px)` }}
      >
        {children}
      </div>
    </div>
  );
};

const OrdersPage = () => {
  const PAGE_SIZE = 6;
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const highlightId = queryParams.get("highlight");
  const orderRefs = useRef({});
  const [confirmCancel, setConfirmCancel] = useState({
    visible: false,
    slug: null,
  });
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  const [shouldRunSwipeDemo, setShouldRunSwipeDemo] = useState(false);
  const [demoTargetOrderId, setDemoTargetOrderId] = useState(null);
  const [deletingAll, setDeletingAll] = useState(false);
  const { openOrderReview } = useOrderReviewModal();
  const [storeImageFailed, setStoreImageFailed] = useState({});

  const handleStarClick = (e, order) => {
    e.stopPropagation();
    if (!isOrderDelivered(order.status)) return;
    if (isOrderRated(order.slug, order.is_rated)) {
      toast.error("تم تقييم هذا الطلب مسبقاً");
      return;
    }
    openOrderReview(order.slug);
  };

  const openCancelModal = (slug) => {
    setConfirmCancel({ visible: true, slug });
  };

  const closeCancelModal = () => {
    setConfirmCancel({ visible: false, slug: null });
  };

  const statusConfig = {
    delivered: {
      label: "تم التوصيل",
      bg: "#DDF8E4",
      color: "#1F7A4D",
      border: "#C4EED1",
      icon: "/assets/images/delivered.svg",
    },
    cancelled: {
      label: "تم الإلغاء",
      bg: "#F8D7DA",
      color: "#7F1D1D",
      border: "#EFB9BF",
      icon: "/assets/images/cancelled.svg",
    },
    confirmed: {
      label: "تم التأكيد",
      bg: "#FFF7D6",
      color: "#8A6420",
      border: "#F2E4A7",
      icon: "/assets/images/confirmed.svg",
    },
    pending: {
      label: "قيد المعالجة",
      bg: "#FDEFD9",
      color: "#8A5A22",
      border: "#F1DBBA",
    },
    preparing: {
      label: "قيد التحضير",
      bg: "#D8EEFF",
      color: "#145E75",
      border: "#BCDDF3",
    },
    delivering: {
      label: "قيد التوصيل",
      bg: "#DDF8E4",
      color: "#1F7A4D",
      border: "#C4EED1",
    },
  };

  const filterTabs = [
    { key: "all", label: "الكل" },
    { key: "delivered", label: "المكتملة" },
    { key: "cancelled", label: "الملغاة" },
    { key: "active", label: "النشطة" },
  ];

  const formatOrderDate = (dateValue) => {
    if (!dateValue) return "—";
    const parsed = new Date(dateValue);
    if (Number.isNaN(parsed.getTime())) return "—";
    return parsed
      .toLocaleString("en-US", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .replace(",", "  -");
  };

  const groupOrderItems = (order) =>
    Object.values(
      (Array.isArray(order?.items) ? order.items : []).reduce((acc, item) => {
        const isDish = !!item?.dish;
        const storeName =
          (isDish ? item?.dish?.restaurant?.name : item?.product?.market?.name) ||
          "المتجر";

        if (!acc[storeName]) {
          acc[storeName] = {
            storeName,
            items: [],
          };
        }

        acc[storeName].items.push(item);
        return acc;
      }, {})
    );



  const fetchOrders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        toast.error("يجب تسجيل الدخول أولاً");
        setLoading(false);
        return;
      }

      const response = await fetch(`${base_url}/api/orders`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (response.ok && result.status) {
        setOrders(result.data.orders);
      } else {
        toast.error("فشل في جلب الطلبات");
      }
    } catch (error) {
      console.error(error);
      toast.error("حدث خطأ في الاتصال بالسيرفر");
    } finally {
      setLoading(false);
    }
  };



  useEffect(() => {
    if (!loading && highlightId && orderRefs.current[highlightId]) {
      orderRefs.current[highlightId].scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
    }
  }, [loading, highlightId]);

  useEffect(() => {
    if (highlightId) {
      setTimeout(() => {
        navigate("/orders", { replace: true });
      }, 3000);
    }
  }, [highlightId]);



  const cancelOrder = async (slug) => {
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        toast.error("يجب تسجيل الدخول أولاً");
        return;
      }

      const response = await fetch(`${base_url}/api/orders/${slug}/cancel`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (response.ok && data.status) {
        toast.success(data.message);








      } else {
        toast.error(data.message || "فشل الإلغاء");
      }
    } catch (e) {
      toast.error("حدث خطأ أثناء الإلغاء");
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    const onReviewed = () => fetchOrders();
    window.addEventListener("order-reviewed", onReviewed);
    return () => window.removeEventListener("order-reviewed", onReviewed);
  }, []);

  useEffect(() => {
    const seen = localStorage.getItem("orders_swipe_hint_seen");
    if (!seen) {
      setShouldRunSwipeDemo(true);
    }
  }, []);

  const handleSwipeDemoFinish = () => {
    setShouldRunSwipeDemo(false);
    setDemoTargetOrderId(null);
    localStorage.setItem("orders_swipe_hint_seen", "1");
  };

  const deleteSingleOrder = async (slug) => {
    if (!slug) {
      toast.error("معرّف الطلب غير صالح");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("يجب تسجيل الدخول أولاً");
        return;
      }

      const encodedSlug = encodeURIComponent(slug);
      const response = await fetch(`${base_url}/api/orders/${encodedSlug}`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      let data = {};
      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (response.ok && (data.status ?? true)) {
        toast.success(data.message || "تم حذف الطلب");
        await fetchOrders();
      } else {
        toast.error(data.message || `تعذر حذف الطلب (رمز الاستجابة: ${response.status})`);
      }
    } catch (error) {
      console.error(error);
      toast.error("حدث خطأ أثناء حذف الطلب");
    }
  };

  const deleteAllOrders = async () => {
    try {
      setDeletingAll(true);
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("يجب تسجيل الدخول أولاً");
        return;
      }

      const response = await fetch(`${base_url}/api/orders/history`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      let data = {};
      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (response.ok && (data.status ?? true)) {
        toast.success(data.message || "تم حذف السجل");
        setConfirmDeleteAll(false);
        await fetchOrders();
      } else {
        toast.error(data.message || `تعذر حذف السجل (رمز الاستجابة: ${response.status})`);
      }
    } catch (error) {
      console.error(error);
      toast.error("حدث خطأ أثناء حذف السجل");
    } finally {
      setDeletingAll(false);
    }
  };

  const filteredOrders = useMemo(() => {
    if (activeFilter === "all") return orders;
    if (activeFilter === "delivered") {
      return orders.filter((o) => o.status === "delivered");
    }
    if (activeFilter === "cancelled") {
      return orders.filter((o) => o.status === "cancelled");
    }
    return orders.filter((o) =>
      ["pending", "confirmed", "preparing", "delivering"].includes(
        String(o.status || "").toLowerCase()
      )
    );
  }, [orders, activeFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE));
  const paginatedOrders = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredOrders.slice(start, start + PAGE_SIZE);
  }, [filteredOrders, currentPage, PAGE_SIZE]);

  useEffect(() => {
    if (!shouldRunSwipeDemo || !paginatedOrders.length) return;
    setDemoTargetOrderId(paginatedOrders[0].id);
  }, [shouldRunSwipeDemo, paginatedOrders]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  return (
    <>
      <Helmet>
        <title>الطلبات - يسلمو</title>
      </Helmet>

      <ResponsiveTopNavigation />
      <div className="min-h-screen bg-[#F7F7F7] font-[cairo]" dir="rtl">
        <div className="mx-auto max-w-4xl px-3 pb-8 pt-6 md:px-4 md:pt-8">

          {loading && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <AppSpinner />
            </div>
          )}

          <div className="mt-12 mb-3  flex items-center justify-between">
            {orders.length > 0 && (
              <button
                type="button"
                onClick={() => setConfirmDeleteAll(true)}
                className="inline-flex items-center gap-1.5 px-3 py-0.5 text-[11px] font-semibold text-[#F93209] transition hover:bg-[#FFF1EE]"
              >
                <Icon name="Trash2" size={15} />
                حذف الكل
              </button>
            )}
          </div>

          <div className="rounded-[8px] ">
            <div className=" grid grid-cols-4 gap-2">
              {filterTabs.map((tab) => {
                const isActiveTab = activeFilter === tab.key;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveFilter(tab.key)}
                    className={`rounded-full border py-0.5 text-[13px] transition ${isActiveTab
                        ? "border-[#F93209] bg-[#F93209] text-white"
                        : "border-[#E0E0E0] bg-white text-[#444]"
                      }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {!loading && filteredOrders.length === 0 && (
            <div className="mt-12 rounded-[18px] bg-white px-4 py-10 text-center text-sm text-gray-500 shadow-sm">
              لا يوجد طلبات حتى الآن
            </div>
          )}
          <div className="mb-4 mt-3 space-y-3 md:space-y-3.5">
            {paginatedOrders.map((order) => {
              const normalizedStatus = String(order.status || "").toLowerCase();
              const status = statusConfig[normalizedStatus] || {
                label: order.status || "حالة الطلب",
                bg: "#F3F4F6",
                color: "#374151",
                border: "#E5E7EB",
              };
              const storeImg = getOrderStoreImage(order);
              const orderRated = isOrderRated(order.slug, order.is_rated);
              const showRatingButton = canShowOrderRatingButton(order);
              const showStoreFallback = !storeImg || storeImageFailed[order.id];
              const groupedStores = groupOrderItems(order);

              return (
                <SwipeToDeleteOrderCard
                  key={order.id}
                  isHighlighted={highlightId == order.id}
                  onDelete={() => deleteSingleOrder(order.slug)}
                  onCardClick={() => navigate(`/orders/${order.slug}`)}
                  runDemo={shouldRunSwipeDemo && demoTargetOrderId === order.id}
                  onDemoFinish={handleSwipeDemoFinish}
                >
                  <div
                    ref={(el) => (orderRefs.current[order.id] = el)}
                    className={`cursor-pointer rounded-[8px] border border-[#ECECEC] bg-white p-3 shadow-sm transition ${
                      highlightId == order.id ? "ring-1 ring-[#E5E7EB] bg-white" : ""
                    }`}
                  >
                    <div className="mb-3 flex items-start justify-between gap-2.5" style={{ direction: "ltr" }}>
                      
                      <div
                        className="inline-flex min-w-fit items-center gap-1 rounded-full border px-2.5 py-1 text-[12px] font-medium"
                        style={{
                          backgroundColor: status.bg,
                          color: status.color,
                          borderColor: status.border,
                        }}
                      >
                        {status.label}
                        {status.icon && (
                          <img
                            src={status.icon}
                            alt=""
                            className="h-4 w-4"
                          />
                        )}
                      </div>

                      <div className='flex justify-between gap-1.5'>

                      <div className="min-w-0 flex-1 text-center" dir="rtl">
                        <div className="truncate text-[15px] font-bold text-[#1F2937]">
                          الطلب - {order.slug}
                        </div>
                        <div className=" text-[12px] text-[#6B7280] text-right">
                          {formatOrderDate(order.created_at)}
                        </div>
                      </div>
                     

                      <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-full border border-[#E5E7EB] bg-white">
                        {!showStoreFallback ? (
                          <img
                            src={storeImg}
                            alt=""
                            className="h-full w-full object-cover"
                            onError={() =>
                              setStoreImageFailed((prev) => ({
                                ...prev,
                                [order.id]: true,
                              }))
                            }
                          />
                        ) : (
                          <FaStoreAlt color="#9CA3AF" size={18} />
                        )}
                      </div>
                      </div>
                    </div>

                    {normalizedStatus === "confirmed" && (
                      <div className="mb-3 flex items-center justify-between rounded-[8px] bg-[#FFF4F2] px-2.5 py-2" style={{ direction: "ltr" }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openCancelModal(order.slug);
                          }}
                          className="rounded-[4px] bg-[#F93209] px-2.5 py-1.5 text-[11px] font-semibold text-white transition hover:bg-[#e22d07]"
                        >
                          إلغاء الطلب
                        </button>
                        <div dir="rtl" className="text-right text-[11px] text-[#D4634B] sm:text-[12px]">
                          متاح الإلغاء حتى يتم قبول الطلب من المندوب
                        </div>
                      </div>
                    )}

                    <div className="space-y-3 border-t border-[#F2F2F2] pt-2">
                      {groupedStores.map((group, index) => (
                        <div
                          key={`${group.storeName}-${index}`}
                          className={index === 0 ? "" : "border-t border-[#F2F2F2] pt-3"}
                        >
                          <div className="text-right text-[15px] font-bold text-[#1F2937] sm:text-[16px]">
                            {group.storeName}
                          </div>

                          <div className=" space-y-1">
                            {group.items.map((item) => {
                              const isDish = !!item?.dish;
                              const name = isDish ? item?.dish?.name : item?.product?.name;
                              const price = item?.price;

                              return (
                                <div key={item.id} className="text-right">
                                  <div className="text-[13px] font-medium text-[#2D3748] sm:text-[14px]">
                                    {name || "عنصر من الطلب"}
                                  </div>
                                  <div className=" text-[13px] font-bold text-[#F97316]">
                                    {price} ل.س
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div
                      style={{ direction: "ltr" }}
                      className={`mt-3 flex items-center gap-3 border-t border-[#F2F2F2] pt-2.5 font-bold ${
                        showRatingButton ? "justify-between" : "justify-end"
                      }`}
                    >
                      {showRatingButton && (
                      <button
                        type="button"
                        onClick={(e) => handleStarClick(e, order)}
                        className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[6px] border border-[#E5E7EB] bg-[#F8F8F8] shadow-sm transition"
                        aria-label={orderRated ? "تم التقييم" : "تقييم الطلب"}
                      >
                        <Icon
                          name="Star"
                          size={18}
                          className={
                            orderRated
                              ? "fill-amber-400 text-amber-400"
                              : "fill-transparent text-gray-400"
                          }
                        />
                      </button>
                      )}

                      <div className="text-right text-[15px] font-bold text-[#1F2937]">
                        <span className="ml-1 text-[#374151]">المجموع :</span>
                        <span className="text-[#F97316]">{order.total_amount} ل.س</span>
                      </div>
                    </div>
                  </div>
                </SwipeToDeleteOrderCard>
              );
            })}
          </div>

          {!loading && filteredOrders.length > 0 && (
            <div className="mb-10 flex items-center justify-center gap-3" dir="rtl">
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className={`flex h-7 w-7 items-center justify-center rounded-[6px] border ${currentPage === 1
                    ? "cursor-not-allowed border-[#D6D6D6] bg-[#FFFFFF] text-[#B9B9B9]"
                    : "border-[#D6D6D6] bg-[#FFFFFF] text-[#555]"
                  }`}
              >
                <Icon name="ChevronRight" size={18} />
              </button>

              <div className="min-w-[52px] rounded-[6px] border border-[#D6D6D6] bg-white px-4  text-center text-[14px] font-medium text-[#222]">
                {currentPage}/{totalPages}
              </div>

              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className={`flex h-7 w-7 items-center justify-center rounded-[6px] border ${currentPage === totalPages
                    ? "cursor-not-allowed border-[#D6D6D6] bg-[#FFFFFF] text-[#B9B9B9]"
                    : "border-[#D6D6D6] bg-[#FFFFFF] text-[#555]"
                  }`}
              >
                <Icon name="ChevronLeft" size={18} />
              </button>
            </div>
          )}
        </div>
      </div>
      {confirmCancel.visible && (
        <div className="fixed inset-0 bg-black/10 flex items-center justify-center z-50 font-[cairo]" onClick={(e) => e.stopPropagation()}>
          <div className="bg-white rounded-lg p-6 w-11/12 max-w-sm text-center" onClick={(e) => e.stopPropagation()}>
            <p className="mb-4 text-gray-800 font-medium">
              هل أنت متأكد من إلغاء هذا الطلب؟
            </p>

            <div className="flex justify-center gap-4">
              <button
                onClick={async () => {
                  closeCancelModal();
                  setLoading(true);
                  await cancelOrder(confirmCancel.slug);
                  await fetchOrders();
                  setLoading(false);
                }}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition"
              >
                نعم
              </button>
              <button
                onClick={closeCancelModal}
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 transition"
              >
                لا
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDeleteAll && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 px-4 font-[cairo]">
          <div className="w-full max-w-md rounded-[8px] bg-white p-5 text-center shadow-xl">
            <button
              type="button"
              onClick={() => setConfirmDeleteAll(false)}
              className="ml-auto mb-2 block text-gray-500"
            >
              <Icon name="X" size={18} />
            </button>
            <div className="mx-auto mb-3 h-[120px] w-[120px] rounded-[6px] flex items-center justify-center">
              <img src="/assets/images/delOrder.svg" alt="trash" className="w-full h-full" />
            </div>
            <h3 className="text-[20px] font-medium font-[cairo] text-[#222] leading-[1.2]">سيتم حذف كافة الطلبات</h3>
            <p className="mt-2 text-[16px] text-[#6b7280] leading-[1.3]">هل أنت متأكد من حذف كافة السجل ؟</p>
            <div className="mt-5 flex items-center gap-3">
              <button
                type="button"
                onClick={deleteAllOrders}
                disabled={deletingAll}
                className="flex-1 rounded-[6px] bg-[#F93209] py-1.5 px-1 text-white font-semibold disabled:opacity-70"
              >
                {deletingAll ? "جارِ الحذف..." : "حذف السجل"}
              </button>
              <button
                type="button"
                onClick={() => setConfirmDeleteAll(false)}
                disabled={deletingAll}
                className="flex-1 rounded-[6px] border border-[#F93209] py-1 px-1 text-[#F93209] font-semibold"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OrdersPage;